<script setup>
const props = defineProps({
  isSignUp: Boolean,
  isAdminSignup: Boolean,
  isForgotPassword: Boolean,
  isResetPassword: Boolean,
  redirectTo: String
});

const client = useSupabaseClient();

const firstName = ref("");
const lastName = ref("");
const email = ref("");
const password = ref("");
const occupation = ref("");
const referralSource = ref("");
const betaFeedback = ref("");

const authError = ref("");
const successMessage = ref("");
const loading = ref(false);

const authResultState = useState('authResultState');

const occupationOptions = ['Builder', 'Contractor', 'Home Owner', 'Municipal Employee', 'Developer', 'Engineer', 'Interior Designer', 'Project Manager', 'Real Estate Agent', 'Architect', 'Other'];

const referralOptions = ['Google', 'LinkedIn', 'Friend or Colleague', 'Industry Event', 'Other'];

// Sign in function with error handling and timeout
async function signIn() {
  loading.value = true;

  // Create a promise that attempts to sign in
  const { data, error } = await client.auth.signInWithPassword({
    email: email.value,
    password: password.value,
  });

  if (error?.message == 'Email not confirmed') {
    // Email not confirmed:

    authResultState.value = {
      success: false,
      waitingForEmailConfirmation: true,
    }

    authError.value = "Please confirm your email before signing in";
    loading.value = false;
  }
  else if (error) {
    // Credentials not valid:

    authResultState.value = {
      success: false,
      waitingForEmailConfirmation: false,
    }

    authError.value = error.message;
    loading.value = false;
  }
  else {
    // Successful sign in:

    // Before redirecting, set the municipality cookie to the user's municipality if exists
    const userId = data.user.id;  
    const userMunicipality = await client.from('profiles').select('municipality').eq('id', userId).single();
    const municipality = useCookie('municipality');
    municipality.value = userMunicipality.data?.municipality || null;

    if (props.redirectTo) {
      window.location.href = props.redirectTo;
    }
    else {
      window.location.href = "/";
    }
  }
}

function validatePassword(password) {
  authError.value = "";
  if (password.value.length < 8) {
    authError.value = authError.value + ("Password must be at least 8 characters.<br>");
  }
  if (!/[A-Z]/.test(password.value)) {
    authError.value = authError.value + ("Password must contain Uppercase letter.<br>");
  }
  if (!/[!@#$% ^&*()\-+={}[\]:;"'<>,.?\/ |\\]/.test(password.value)) {
    authError.value = authError.value + ("Password must contain a special character such as !@#$.");
  }
  if (authError.value.length > 0) {
    if (authError.value.endsWith("<br>")) {
      authError.value = authError.value.slice(0, -4);
    }
    return false;
  }
  return true;
}

// Send sign up request to supabase, and insert rows into the auth and profiles tables
async function signUp() {
  // Perform form validation first
  if (!firstName.value || !lastName.value || !email.value || !password.value) {
    authError.value = "Please fill in all fields";
    return;
  }
  if (!validatePassword(password)) {
    return;
  }

  try {
    loading.value = true;

    // Send sign up request to supabase, which inserts a row into the auth.users table (supabase's premade table in auth schema)
    const { data: newUser, error: signUpError } = await client.auth.signUp({
      email: email.value,
      password: password.value,
    });
    if (signUpError) throw signUpError;

    // Now, insert a row into the profiles table (public schema)
    const { error: insertError } = await client
      .from("profiles")
      .insert({
        id: newUser.user.id,
        email: email.value,
        first_name: firstName.value,
        last_name: lastName.value,
        occupation: occupation.value,
        referral_source: referralSource.value,
        beta_feedback: betaFeedback.value,
        subscription: "builder", // B2C signup is only for builders right now
        municipality: "vancouver"
      });

    if (insertError) throw insertError;
  }
  catch (error) {
    if (error.code == '23505') {
      // Unique constraint violation:

      authError.value = "Sorry, it looks like an account with that email already exists";
      authResultState.value = {
        success: false,
        waitingForEmailConfirmation: false,
      }
    }
    else {
      // Other error:

      authError.value = error.message;
      authResultState.value = {
        success: false,
        waitingForEmailConfirmation: false,
      }
    }
  }
  finally {
    if (!authError.value) {
      // Successful sign up:

      authResultState.value = {
        success: true,
        waitingForEmailConfirmation: true,
      }

      window.location.href = "/";
    }

    loading.value = false;
  }
}

async function adminSignUp() {
  // Perform form validation first
  authError.value = "";
  if (!firstName.value || !lastName.value || !email.value || !password.value) {
    authError.value = "Please fill in all fields";
    return;
  }

  try {
    loading.value = true;

    const adminSignupResponse = await useFetch("/api/adminSignup", {
      params: {
        email: email.value,
        password: password.value
      },
    });

    if (adminSignupResponse.error.value) {
      throw adminSignupResponse.error.value;
    }
    else if (!adminSignupResponse.data.value.userId) {
      throw new Error("No user ID returned from server");
    }

    // Now, insert a row into the profiles table (public schema)
    const { error: insertError } = await client
      .from("profiles")
      .insert({
        id: adminSignupResponse.data.value.userId,
        email: email.value,
        first_name: firstName.value,
        last_name: lastName.value,
        occupation: occupation.value,
        referral_source: referralSource.value,
        beta_feedback: betaFeedback.value
      })
      .eq("email", email.value);

    if (insertError) throw insertError;
  }
  catch (error) {
    if (error.code == '23505') {
      // Unique constraint violation:

      authError.value = "Sorry, it looks like an account with that email already exists";
      authResultState.value = {
        success: false,
        waitingForEmailConfirmation: false,
      }
    }
    else {
      // Other error:

      authError.value = error.message;
      authResultState.value = {
        success: false,
        waitingForEmailConfirmation: false,
      }
    }
  }
  finally {
    if (!authError.value) {
      // Successful sign up:

      authResultState.value = {
        success: true,
        waitingForEmailConfirmation: false,
      }
    }

    loading.value = false;
  }
}
async function sendPasswordResetLink() {
  if (!email.value) {
    authError.value = "Please enter an email address";
    return;
  }
  try {
    loading.value = true;
    const { data, error } = await client.auth.resetPasswordForEmail(email.value, { redirectTo: window.location.href + "/forgot-password" });
    if (error) throw error;

    successMessage.value = "If an account exists for that email, you'll receive a password reset link. Please check the spam folder as well.";
    authError.value = "";
  } catch (error) {
    if (error?.message == "Unable to validate email address: invalid format") {
      authError.value = "Invalid email address format.";
    } else {
      authError.value = "Error, please try at another time.";
    }
    successMessage.value = "";
  } finally {
    loading.value = false;
  }
}

async function resetPassword() {
  if (!validatePassword(password)) {
    return;
  }
  try {
    loading.value = true;
    const { data, error } = await client.auth.updateUser({
      password: password.value,
    });
    if (error) throw error;
    signOut();
    window.location.href = "/";
  } catch (error) {
    if (error?.status == 400) {
      authError.value = "Password Reset Failed: Expired or Invalid Link";
    } else {
      //any other error
      authError.value = "Password Reset Failed, please try at another time";
    }
  } finally {
    loading.value = false;
  }
}

async function signOut() {
  const { error } = await client.auth.signOut();
}

</script>

<template>
  <div class="flex flex-col space-y-4">
    <p v-if="isForgotPassword && !isSignUp">Enter the email associated with your account to receive a password reset
      link.</p>
    <UInput v-if="isSignUp || isAdminSignup" v-model="firstName" placeholder="First Name *" size="xl"
      :autocomplete="isAdminSignup ? 'off' : 'given-name'" />

    <UInput v-if="isSignUp || isAdminSignup" v-model="lastName" placeholder="Last Name *" size="xl"
      :autocomplete="isAdminSignup ? 'off' : 'family-name'" />

    <!-- Builder Signup Questions -->
    <template v-if="isSignUp">
      <USelect v-model="occupation" :items="occupationOptions" placeholder="What is your occupation?" size="xl" />

      <USelect v-model="referralSource" :items="referralOptions" placeholder="How did you hear about Siteability?"
        size="xl" />

      <UTextarea v-model="betaFeedback" placeholder="What are you hoping to use Siteability for?" size="xl" :rows="3" />
    </template>

    <UInput v-if="!isResetPassword" icon="heroicons:envelope" type="email" v-model="email"
      :placeholder="isSignUp || isAdminSignup ? 'Email *' : 'Email'" size="xl"
      :autocomplete="isAdminSignup ? 'off' : 'email'" />

    <Password v-if="!isForgotPassword" v-model="password"
      :placeholder="isSignUp || isAdminSignup ? 'Password *' : 'Password'" size="xl" :isAdminSignup="isAdminSignup"
      @keydown.enter="isAdminSignup ? adminSignUp() : isSignUp ? signUp() : signIn()" />

    <p v-if="authError"
      class="text-red-600 p-3 rounded-lg bg-red-50/80 backdrop-blur-sm text-sm border-l-4 border-red-500 shadow-sm"
      v-html="authError">
    </p>
    <p v-if="successMessage && isForgotPassword && !isSignUp"
      class="text-green-600 p-3 rounded-lg bg-green-50/80 backdrop-blur-sm text-sm border-l-4 border-green-500 shadow-sm"
      v-html="successMessage">
    </p>

    <UButton :disabled="loading" :loading="loading"
      @click="isAdminSignup ? adminSignUp() : isSignUp ? signUp() : isForgotPassword ? sendPasswordResetLink() : isResetPassword ? resetPassword() : signIn()"
      block size="xl">
      <span v-if="isAdminSignup">Create User</span>
      <span v-else-if="isSignUp">Sign Up</span>
      <span v-else-if="isForgotPassword">Send Password Reset Link</span>
      <span v-else-if="isResetPassword">Change Password</span>
      <span v-else>Sign In</span>
    </UButton>
  </div>
</template>